import { Routes } from '@angular/router';

import { AuthGuard } from './core/guards/auth/auth.guard';
import { RedirectService } from '@guards/dashboard/redirect.guard';
import { CreateEmergencieGuard } from '@guards/emergencie/createEmergencie.guard';
import { RoleSecurityGuard } from '@guards/role/role.guard';
import { PermissionToActivityGuard } from '@guards/prioritizedActivity/permissionToActivity.guard';
import { ChangePasswordPageComponent } from './features/auth/pages/changePasswordPage/changePasswordPage.component';
// DEV-ONLY: Translation keys manager — remove before production
import { TranslationKeysComponent } from './features/dashboard/interns/translation-keys/translation-keys.component';
import { workflowTypeGuard } from '@guards/workflows/WorkflowType-guard';

/** Árbol de rutas principal de la aplicación. Define los tres paneles de rol (configuration, executor, implementer) y las rutas de autenticación. */
export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    canActivate: [RedirectService],
    loadComponent: () =>
      import('./features/layout/pages/panelPage/panelPage.component').then(
        (m) => m.PanelPageComponent,
      ),
  },
  {
    path: 'auth',
    children: [
      {
        path: 'login',
        loadComponent: () =>
          import('./features/auth/pages/loginPage/loginPage.component').then(
            (m) => m.LoginPageComponent,
          ),
        canActivate: [RedirectService],
      },
      {
        path: 'recover-password',
        loadComponent: () =>
          import('./features/auth/components/recoverPwd/recoverPwd.component').then(
            (m) => m.RecoverPwdComponent,
          ),
      },
    ],
  },

  // Administrator

  {
    path: 'configuration',
    loadComponent: () =>
      import('./features/layout/pages/panelPage/panelPage.component').then(
        (m) => m.PanelPageComponent,
      ),
    canActivate: [AuthGuard, RoleSecurityGuard],
    data: { role: 'administrator' },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./features/dashboard/pages/admin/admin-dashboard.component').then(
            (m) => m.AdminDashboardComponent,
          ),
      },
      {
        path: 'my-profile',
        loadComponent: () =>
          import('./features/auth/my-profile/my-profile.component').then(
            (m) => m.MyProfileComponent,
          ),
      },
      {
        path: 'change-password',
        component: ChangePasswordPageComponent,
      },
      {
        path: 'system',
        loadComponent: () =>
          import('./features/dashboard/interns/system-settings/systemSetting/systemSetting.component').then(
            (m) => m.SystemSettingComponent,
          ),
      },
      {
        path: 'workflow',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./features/dashboard/interns/workFlowSetting/pages/WorkflowsList/workflowList').then(
                (m) => m.WorkflowList,
              ),
          },
          {
            path: ':workflowType/:workflowDetail',
            canMatch: [workflowTypeGuard],
            loadComponent: () =>
              import('./features/dashboard/interns/workFlowSetting/pages/WorkflowDetail/workflowDetail').then(
                (m) => m.WorkflowDetail,
              ),
          },
          {
            path: ':workflowType',
            canMatch: [workflowTypeGuard],
            loadComponent: () =>
              import('./features/dashboard/interns/workFlowSetting/pages/WorkflowType/workflowType').then(
                (m) => m.WorkflowType,
              ),
          },
        ],
      },
      {
        path: 'roles',
        loadComponent: () =>
          import('./features/dashboard/interns/roles/list-roles-settings/list-roles-settings.component').then(
            (m) => m.ListRolesSettingsComponent,
          ),
      },
      {
        path: 'interface',
        loadComponent: () =>
          import('./features/dashboard/interns/adminInterface/adminInterface.component').then(
            (m) => m.AdminInterfaceComponent,
          ),
      },
      {
        path: 'sectors',
        loadComponent: () =>
          import('./features/dashboard/interns/admin-sectors/page/SectorSettingIntern.component').then(
            (m) => m.SectorSettingInternComponent,
          ),
      },
      {
        path: 'users',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./features/dashboard/interns/users-admin/page/usersList/usersList.component').then(
                (m) => m.UsersListComponent,
              ),
          },
          {
            path: 'action/:action',
            loadComponent: () =>
              import('./features/dashboard/interns/users-admin/page/user-actions/user-actions.component').then(
                (m) => m.UserActionsComponent,
              ),
          },
          {
            path: 'action/:action/:id',
            loadComponent: () =>
              import('./features/dashboard/interns/users-admin/page/user-actions/user-actions.component').then(
                (m) => m.UserActionsComponent,
              ),
          },
        ],
      },
      {
        path: 'language',
        loadComponent: () =>
          import('./features/dashboard/interns/languages/page/translation-manager/translation-manager.component').then(
            (m) => m.TranslationManagerComponent,
          ),
      },
      {
        path: 'translation-keys',
        loadComponent: () =>
          import('./features/dashboard/interns/translation-keys/translation-keys.component').then(
            (m) => m.TranslationKeysComponent,
          ),
      },
      {
        path: 'emergencie-types',
        loadComponent: () =>
          import('./features/dashboard/pages/emergencieTypesSettingPage/emergencieTypesSettingPage.component').then(
            (m) => m.EmergencieTypesSettingPageComponent,
          ),
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./features/dashboard/interns/emergencieTypes/emergencieTypes.component').then(
                (m) => m.EmergencieTypesComponent,
              ),
          },
        ],
      },
      {
        path: 'currency',
        loadComponent: () =>
          import('./features/dashboard/interns/currency/page/currency-type.component').then(
            (m) => m.CurrencyTypeComponent,
          ),
      },
      {
        path: 'emergencies',
        loadComponent: () =>
          import('./features/emergencie/pages/emergenciePage/emergenciePage.component').then(
            (m) => m.EmergenciePageComponent,
          ),
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./features/emergencie/components/listEmergencie/listEmergencie.component').then(
                (m) => m.ListEmergencieComponent,
              ),
          },
          {
            path: 'create-emergencie',
            loadComponent: () =>
              import('./features/emergencie/interns/emergencieCreation/emergencieCreationIntern.component').then(
                (m) => m.EmergencieCreationInternComponent,
              ),
          },
          {
            path: ':id',
            loadComponent: () =>
              import('./features/emergencie/emergencyDetail/page/emergencieDetailLayout/EmergencieDetailIntern.component').then(
                (m) => m.EmergencieDetailInternComponent,
              ),
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./features/emergencie/emergencyDetail/page/emergencieDetailPanel/emergencieDetail.component').then(
                    (m) => m.EmergencieDetailComponent,
                  ),
              },
              {
                path: 'credit-caps',
                loadComponent: () =>
                  import('./features/emergencie/interns/manageCreditCaps/manageCreditCaps.component').then(
                    (m) => m.ManageCreditCapsComponent,
                  ),
              },
              {
                path: ':id',
                loadComponent: () =>
                  import('./features/prioritized-activity/page/prioritized-activity-page').then(
                    (m) => m.PrioritizedActivityPage,
                  ),
                canActivate: [PermissionToActivityGuard],
              },
            ],
          },
        ],
      },
      {
        path: 'entities',
        loadComponent: () =>
          import('./features/entities/pages/layout/layout-entities').then(
            (m) => m.LayoutEntities,
          ),
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./features/entities/pages/admin/publicEntities.component').then(
                (m) => m.PublicEntitiesComponent,
              ),
          },
          {
            path: 'create-entity',
            loadComponent: () =>
              import('./features/entities/pages/create-entity/create-entity').then(
                (m) => m.CreateEntity,
              ),
          },
        ],
      },
      {
        path: 'teams',
        loadComponent: () =>
          import('./features/dashboard/interns/teams/entityTeams.component').then(
            (m) => m.EntityTeamsComponent,
          ),
      },
      {
        path: 'auditor',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./features/auditor/pages/dashboard/auditor-dashboard.component').then(
                (m) => m.AuditorDashboardComponent,
              ),
          },
          {
            path: ':id',
            loadComponent: () =>
              import('./features/auditor/pages/acquisition-detail/auditor-acquisition-detail.component').then(
                (m) => m.AuditorAcquisitionDetailComponent,
              ),
          },
        ],
      },
      {
        path: 'financial-entity',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./features/financial-entity/pages/financial-entity-dashboard.component').then(
                (m) => m.FinancialEntityDashboardComponent,
              ),
          },
          {
            path: 'emergency/:emergencyId',
            loadComponent: () =>
              import(
                './features/financial-entity/pages/financial-entity-emergency-requests.component'
              ).then((m) => m.FinancialEntityEmergencyRequestsComponent),
          },
          {
            path: 'executions/:emergencyId',
            loadComponent: () =>
              import(
                './features/financial-entity/pages/financial-entity-execution-review.component'
              ).then((m) => m.FinancialEntityExecutionReviewComponent),
          },
        ],
      },
      // DEV-ONLY: Translation keys manager — remove before production
      {
        path: 'translation-keys',
        component: TranslationKeysComponent,
      },
    ],
  },

  // Executor

  {
    path: 'executor',
    loadComponent: () =>
      import('./features/layout/pages/panelPage/panelPage.component').then(
        (m) => m.PanelPageComponent,
      ),
    canActivate: [AuthGuard, RoleSecurityGuard],
    data: { role: 'executor' },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./features/gestion-panel/pages/gestion-panel.component').then(
            (m) => m.GestionPanelComponent,
          ),
        data: { role: 'executor' },
      },
      {
        path: 'financial',
        loadComponent: () =>
          import('./features/financial-wallet/layout/financial-page/financial-page.component').then(
            (m) => m.FinancialPageComponent,
          ),
        data: { role: 'executor' },
        children: [
          {
            path: 'coverage-sources',
            loadComponent: () =>
              import('./features/financial-wallet/pages/coverage-sources/coverage-sources.component').then(
                (m) => m.CoverageSourcesComponent,
              ),
          },
          {
            path: 'insurance-policy',
            loadComponent: () =>
              import('./features/financial-wallet/pages/insurance-policy/insurance-policy.component').then(
                (m) => m.InsurancePolicyComponent,
              ),
          },
          {
            path: 'post-disaster-credit',
            loadComponent: () =>
              import('./features/financial-wallet/pages/post-disaster-credits/post-disaster-credits.component').then(
                (m) => m.PostDisasterCreditsComponent,
              ),
          },
        ],
      },
      {
        path: 'add-financial-source',
        loadComponent: () =>
          import('./features/financial-wallet/layout/add-financial-layout/add-financial-layout.component').then(
            (m) => m.AddFinancialLayoutComponent,
          ),
        children: [
          {
            path: 'insurance-policy',
            loadComponent: () =>
              import('./features/financial-wallet/interns/add-insurance-policy/add-insurance-policy.component').then(
                (m) => m.AddInsurancePolicyComponent,
              ),
          },
          {
            path: 'post-disaster-credit',
            loadComponent: () =>
              import('./features/financial-wallet/interns/add-post-disaster-credit/add-post-disaster-credit.component').then(
                (m) => m.AddPostDisasterCreditComponent,
              ),
          },
          // El crédito contingente tiene pantalla propia y va antes que la ruta paramétrica:
          // Angular resuelve por orden, así que `credit` nunca cae en el formulario genérico.
          {
            path: 'coverage-source/credit',
            loadComponent: () =>
              import('./features/financial-wallet/interns/contingent-credit/contingent-credit.component').then(
                (m) => m.ContingentCreditComponent,
              ),
            data: { role: 'executor', mode: 'create' },
          },
          {
            path: 'coverage-source/:slug',
            loadComponent: () =>
              import('./features/financial-wallet/interns/add-financial-source/add-financial-source.component').then(
                (m) => m.AddFinancialSourceComponent,
              ),
            data: { role: 'executor', mode: 'create' },
          },
        ],
      },
      {
        path: 'edit-financial-source',
        loadComponent: () =>
          import('./features/financial-wallet/layout/add-financial-layout/add-financial-layout.component').then(
            (m) => m.AddFinancialLayoutComponent,
          ),
        data: { role: 'executor' },
        children: [
          {
            path: 'coverage-source/:id',
            loadComponent: () =>
              import('./features/financial-wallet/interns/contingent-credit/contingent-credit.component').then(
                (m) => m.ContingentCreditComponent,
              ),
            data: { role: 'executor', mode: 'edit' },
          },
          // La edición por tipo va con dos segmentos (`:slug/:id`), así que nunca colisiona con la
          // ruta de crédito contingente de arriba, que solo lleva `:id`.
          {
            path: 'coverage-source/:slug/:id',
            loadComponent: () =>
              import('./features/financial-wallet/interns/add-financial-source/add-financial-source.component').then(
                (m) => m.AddFinancialSourceComponent,
              ),
            data: { role: 'executor', mode: 'edit' },
          },
        ],
      },
      {
        path: 'emergencies',
        loadComponent: () =>
          import('./features/emergencie/pages/emergenciePage/emergenciePage.component').then(
            (m) => m.EmergenciePageComponent,
          ),
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./features/emergencie/components/listEmergencie/listEmergencie.component').then(
                (m) => m.ListEmergencieComponent,
              ),
          },
          {
            path: 'create-emergencie',
            loadComponent: () =>
              import('./features/emergencie/interns/emergencieCreation/emergencieCreationIntern.component').then(
                (m) => m.EmergencieCreationInternComponent,
              ),
            canActivate: [CreateEmergencieGuard],
            // El modo se declara explícitamente en lugar de deducirse del segmento de
            // la URL, para que renombrar la ruta no cambie el comportamiento del formulario.
            data: { mode: 'create' },
          },

          {
            path: ':idEmergency',
            loadComponent: () =>
              import('./features/emergencie/emergencyDetail/page/emergencieDetailLayout/EmergencieDetailIntern.component').then(
                (m) => m.EmergencieDetailInternComponent,
              ),
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./features/emergencie/emergencyDetail/page/emergencieDetailPanel/emergencieDetail.component').then(
                    (m) => m.EmergencieDetailComponent,
                  ),
              },
              {
                path: 'edit-emergencie',
                loadComponent: () =>
                  import('./features/emergencie/interns/emergencieCreation/emergencieCreationIntern.component').then(
                    (m) => m.EmergencieCreationInternComponent,
                  ),
                canActivate: [CreateEmergencieGuard],
                data: { mode: 'edit' },
              },
              {
                path: 'credit-caps',
                loadComponent: () =>
                  import('./features/emergencie/interns/manageCreditCaps/manageCreditCaps.component').then(
                    (m) => m.ManageCreditCapsComponent,
                  ),
              },
              // Va antes que `:idActivity` porque, si no, la ruta paramétrica captura el
              // segmento literal y lo somete a `PermissionToActivityGuard`.
              {
                path: 'process-request',
                loadComponent: () =>
                  import('./features/process-request/page/process-request-page').then(
                    (m) => m.ProcessRequestPage,
                  ),
              },
              {
                path: 'conciliation',
                loadComponent: () =>
                  import('./features/conciliation/page/conciliation-page').then(
                    (m) => m.ConciliationPage,
                  ),
              },
              {
                path: ':idActivity',
                loadComponent: () =>
                  import('./features/prioritized-activity/page/prioritized-activity-page').then(
                    (m) => m.PrioritizedActivityPage,
                  ),
                canActivate: [PermissionToActivityGuard],
                // El ejecutor ve la actividad igual que el implementador, sin sus acciones.
                data: { sharedActivityView: true },
              },
            ],
          },
        ],
      },
      {
        path: 'entities',
        loadComponent: () =>
          import('./features/entities/pages/admin/publicEntities.component').then(
            (m) => m.PublicEntitiesComponent,
          ),
      },
      {
        path: 'reports',
        loadComponent: () =>
          import('./features/reports/pages/reportPage/reportPage.component').then(
            (m) => m.ReportPageComponent,
          ),
      },
      {
        path: 'financial-entities',
        loadComponent: () =>
          import('./features/dashboard/pages/panel-UIEntity/Panel-UIEntity.component').then(
            (m) => m.PanelUIEntityComponent,
          ),
      },
      {
        path: 'my-profile',
        loadComponent: () =>
          import('./features/auth/my-profile/my-profile.component').then(
            (m) => m.MyProfileComponent,
          ),
      },
      {
        path: 'change-password',
        component: ChangePasswordPageComponent,
      },
    ],
  },

  // Implementor

  {
    path: 'implementer',
    loadComponent: () =>
      import('./features/layout/pages/panelPage/panelPage.component').then(
        (m) => m.PanelPageComponent,
      ),
    canActivate: [AuthGuard, RoleSecurityGuard],
    data: { role: 'implementer' },
    // Vistas propias del implementador (maqueta con mocks, ver `ImplementerService`).
    // `PermissionToActivityGuard` en el detalle; hoy validarían IDs mock contra el API real.
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./features/implementer/pages/implementer-dashboard/implementer-dashboard.component').then(
            (m) => m.ImplementerDashboardComponent,
          ),
        data: { role: 'implementer' },
      },
      {
        path: 'emergencies',
        loadComponent: () =>
          import('./features/emergencie/pages/emergenciePage/emergenciePage.component').then(
            (m) => m.EmergenciePageComponent,
          ),
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./features/implementer/pages/implementer-emergencies/implementer-emergencies.component').then(
                (m) => m.ImplementerEmergenciesComponent,
              ),
          },
          {
            path: ':idEmergency',
            loadComponent: () =>
              import('./features/implementer/pages/implementer-emergency-detail/implementer-emergency-detail.component').then(
                (m) => m.ImplementerEmergencyDetailComponent,
              ),
          },
          {
            path: ':idEmergency/:idActivity',
            loadComponent: () =>
              import('./features/implementer/pages/implementer-activity-detail/implementer-activity-detail.component').then(
                (m) => m.ImplementerActivityDetailComponent,
              ),
          },
        ],
      },
      {
        path: 'my-profile',
        loadComponent: () =>
          import('./features/auth/my-profile/my-profile.component').then(
            (m) => m.MyProfileComponent,
          ),
      },
      {
        path: 'change-password',
        component: ChangePasswordPageComponent,
      },
    ],
  },

  // Auditor

  {
    path: 'auditor',
    loadComponent: () =>
      import('./features/layout/pages/panelPage/panelPage.component').then(
        (m) => m.PanelPageComponent,
      ),
    canActivate: [AuthGuard, RoleSecurityGuard],
    data: { role: 'auditor' },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./features/auditor/pages/dashboard/auditor-dashboard.component').then(
            (m) => m.AuditorDashboardComponent,
          ),
        data: { role: 'auditor' },
      },
      // Antes de ':id' para que 'reports' no se tome como id de adquisición.
      {
        path: 'reports',
        loadComponent: () =>
          import('./features/reports/pages/reportPage/reportPage.component').then(
            (m) => m.ReportPageComponent,
          ),
        data: { role: 'auditor' },
      },
      {
        path: ':id',
        loadComponent: () =>
          import('./features/auditor/pages/acquisition-detail/auditor-acquisition-detail.component').then(
            (m) => m.AuditorAcquisitionDetailComponent,
          ),
        data: { role: 'auditor' },
      },
    ],
  },

  // Entidad financiera

  {
    path: 'financial-entity',
    loadComponent: () =>
      import('./features/layout/pages/panelPage/panelPage.component').then(
        (m) => m.PanelPageComponent,
      ),
    canActivate: [AuthGuard, RoleSecurityGuard],
    data: { role: 'entidad_financiera' },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./features/financial-entity/pages/financial-entity-dashboard.component').then(
            (m) => m.FinancialEntityDashboardComponent,
          ),
        data: { role: 'entidad_financiera' },
      },
      {
        path: 'emergency/:emergencyId',
        loadComponent: () =>
          import(
            './features/financial-entity/pages/financial-entity-emergency-requests.component'
          ).then((m) => m.FinancialEntityEmergencyRequestsComponent),
        data: { role: 'entidad_financiera' },
      },
      {
        path: 'executions/:emergencyId',
        loadComponent: () =>
          import(
            './features/financial-entity/pages/financial-entity-execution-review.component'
          ).then((m) => m.FinancialEntityExecutionReviewComponent),
        data: { role: 'entidad_financiera' },
      },
    ],
  },
];
